import { Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { roundToTwo } from '../../../MACRA_1/components/InputForm/Geometry/utils';
import { IVerticalDropsResults } from '../../../MACRA_2/api/IApiResponse';
import { IProjectMacra2 } from '../../../MACRA_2/store/project/projectSlice';
import { conversionLabel } from '../../../conversion';
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import { styles } from '../PDFStyle';
import PDFTable from '../PDFTable/PDFTable';
import { getVerticalDropsType } from '../../../utils/utils';
import { EVerticalDropsTypes } from '../../../MACRA_2/enum/general';
import BasinDesignResults from './PDFVerticalBasinDesign';

interface IPressureResultsProps {
    project: IProjectMacra2;
    projectInfo: IProjectInfoForm;
    results: IVerticalDropsResults;
}

const PDFStaticVertical = (props: IPressureResultsProps) => {
    const { t } = useTranslation();
    const { project, projectInfo, results } = props;
    const showUpliftPressure =
        getVerticalDropsType(project.stillingBasin) === EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth ||
        getVerticalDropsType(project.stillingBasin) === EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth;
    const stabilityChecks = [
        [t('output_static_vertical_safety_against_sliding'), '@FS(sl)', `${roundToTwo(results.staticResult.stabilityChecksOutput.safetyFactorAgainstSliding)}`],
        [t('output_static_vertical_safety_against_overturning'), '@FS(ov)', `${roundToTwo(results.staticResult.stabilityChecksOutput.safetyFactorAgainstOverturning)}`],
        [t('output_static_vertical_safety_against_seepage'), '@FS(see)', `${roundToTwo(results.staticResult.stabilityChecksOutput.safetyFactorAgainstSiphoning)}`],
    ];
    if (showUpliftPressure)
        stabilityChecks.push([
            t('output_static_vertical_safety_against_uplift_pressure'),
            '@FS(up)',
            `${roundToTwo(results.staticResult.stabilityChecksOutput.safetyCoefficientAgainstUpliftPressure)}`,
        ]);
    const soilThrust = [
        [t('output_static_vertical_active_thrust'), '@K(a)', `${roundToTwo(results.staticResult.soilThrustOutput.activeThrustCoefficient)}`, '-'],
        [
            t('output_static_vertical_total_height'),
            '@h(w)',
            `${roundToTwo(results.staticResult.soilThrustOutput.totalHeightOfTheStructure!)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_soil_thrust_upstream'),
            '@P(am)',
            `${roundToTwo(results.staticResult.soilThrustOutput.soilThrustOfTheUpstreamBackfill)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_horizontal_upstream'),
            '@P(amh)',
            `${roundToTwo(results.staticResult.soilThrustOutput.horizontalComponentOfUpstreamThrust)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [t('output_static_vertical_lever_arm_Pamh'), '@b(Pamh)', `${roundToTwo(results.staticResult.soilThrustOutput.leverArmOfPamh)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [
            t('output_static_vertical_moment_horizontal_upstream'),
            '@b(Pamh)',
            `${roundToTwo(results.staticResult.soilThrustOutput.momentOfHorizontalUpstreamThrust)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [t('output_static_vertical_lever_arm_Pamv'), '@b(Pamv)', `${roundToTwo(results.staticResult.soilThrustOutput.leverArmOfPamv)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [
            t('output_static_vertical_moment_vertical_upstream'),
            '@b(Pamv)',
            `${roundToTwo(results.staticResult.soilThrustOutput.momentOfVerticalUpstreamThrust)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_downstream_soil'),
            '@P(av)',
            `${roundToTwo(results.staticResult.soilThrustOutput.downstreamSoilActiveThrust)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [t('output_static_vertical_lever_arm_Pav'), '@b(Pav)', `${roundToTwo(results.staticResult.soilThrustOutput.leverArmOfPav)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [
            t('output_static_vertical_moment_downstream'),
            '@M(Pav)',
            `${roundToTwo(results.staticResult.soilThrustOutput.momentOfDownstreamThrust)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
    ];

    const weights = [
        [
            t('output_static_vertical_gabion_weir'),
            t('output_static_vertical_weir_unit_weight'),
            'WG',
            `${roundToTwo(results.staticResult.weightsOutput.weirUnitWeight)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            '',
            t('output_static_vertical_lever_arm_weir_weight'),
            '@b(wg)',
            `${roundToTwo(results.staticResult.weightsOutput.leverArmOfTheWeirWeight)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            '',
            t('output_static_vertical_moment_weir_weight'),
            '@M(wg)',
            `${roundToTwo(results.staticResult.weightsOutput.momentOfTheWeirWeight)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_soil'),
            t('output_static_vertical_soil_unit_weight'),
            'WS',
            `${roundToTwo(results.staticResult.weightsOutput.soilUnitWeight)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            '',
            t('output_static_vertical_lever_arm_soil_weight'),
            '@b(ws)',
            `${roundToTwo(results.staticResult.weightsOutput.leverArmOfTheSoilWeight)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            '',
            t('output_static_vertical_moment_soil_weight'),
            '@M(ws)',
            `${roundToTwo(results.staticResult.weightsOutput.momentOfTheSoilWeight)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_water'),
            t('output_static_vertical_water_unit_weight'),
            'WW',
            `${roundToTwo(results.staticResult.weightsOutput.waterUnitWeight)}`,
            `${conversionLabel.kN_lbf[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            '',
            t('output_static_vertical_lever_arm_water_weight'),
            '@b(ww)',
            `${roundToTwo(results.staticResult.weightsOutput.leverArmOfTheWaterWeight)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            '',
            t('output_static_vertical_moment_water_weight'),
            '@M(ww)',
            `${roundToTwo(results.staticResult.weightsOutput.momentOfTheWaterWeight)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
    ];

    const resultingForcesMoments = [
        [
            t('output_static_vertical_normal_force'),
            'N',
            `${roundToTwo(results.staticResult.resultingForcesAndMomentsOutput.normalForceOnTheFoundation)}`,
            `${conversionLabel.kN_lbf[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_shear_stress'),
            'T',
            `${roundToTwo(results.staticResult.resultingForcesAndMomentsOutput.shearStressOnTheFoundation)}`,
            `${conversionLabel.kN_lbf[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_sum_stabilizing'),
            '@M(stab)',
            `${roundToTwo(results.staticResult.resultingForcesAndMomentsOutput.sumOfStabilizingMoments)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_sum_destibilizing'),
            '@M(rib)',
            `${roundToTwo(results.staticResult.resultingForcesAndMomentsOutput.sumOfDestibilizingMoments)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
    ];
    const hydrostaticPressure = [
        [
            t('output_static_vertical_upstream_hydrostatic_pressure'),
            '@S(wm)',
            `${roundToTwo(results.staticResult.hydrostaticPressureOutput.upstreamHydrostaticPressure)}`,
            `${conversionLabel.kN_lbf[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_lever_arm_upstream_hydrostatic_pressure'),
            '@b(Swm)',
            `${roundToTwo(results.staticResult.hydrostaticPressureOutput.leverArmOfUpstreamHydrostaticPressure)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_moment_upstream_hydrostatic_pressure'),
            '@M(Swm)',
            `${roundToTwo(results.staticResult.hydrostaticPressureOutput.momentOfUpstreamHydrostaticPressure)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_downstream_hydrostatic_pressure'),
            '@S(wv)',
            `${roundToTwo(results.staticResult.hydrostaticPressureOutput.downstreamHydrostaticPressure)}`,
            `${conversionLabel.kN_lbf[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_lever_arm_downstream_hydrostatic_pressure'),
            '@b(Swv)',
            `${roundToTwo(results.staticResult.hydrostaticPressureOutput.leverArmOfDownstreamHydrostaticPressure)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_moment_downstream_hydrostatic_pressure'),
            '@M(Swv)',
            `${roundToTwo(results.staticResult.hydrostaticPressureOutput.momentOfDownstreamHydrostaticPressure)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
    ];

    const upliftPressure = [
        [
            t('output_static_vertical_filtration_length'),
            '@L(f)',
            `${roundToTwo(results.staticResult.upliftPressureOutput.filtrationLength)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_upstream_uplift_pressure'),
            '@S(pwm)',
            `${roundToTwo(results.staticResult.upliftPressureOutput.upstreamUpliftPressure)}`,
            `${conversionLabel.kNm_lbfft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_downstream_uplift_pressure'),
            '@S(pwv)',
            `${roundToTwo(results.staticResult.upliftPressureOutput.downstreamUpliftPressure)}`,
            `${conversionLabel.kNm_lbfft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_hydraulic_uplift'),
            '@V(w)',
            `${roundToTwo(results.staticResult.upliftPressureOutput.hydraulicUplift)}`,
            `${conversionLabel.kN_lbf[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_lever_arm_hydraulic_uplift'),
            '@b(Vw)',
            `${roundToTwo(results.staticResult.upliftPressureOutput.leverArmOfHydraulicUplift)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [
            t('output_static_vertical_moment_hydraulic_uplift'),
            '@M(Vw)',
            `${roundToTwo(results.staticResult.upliftPressureOutput.momentOfTheHydraulicUplift)}`,
            `${conversionLabel.kN_m_lbf_ft[projectInfo.unitOfMeasure as number]}`,
        ],
    ];
    return (
        <View>
            <View style={styles.rowSpaced}>
                <Text style={styles.boldGreen}>{t('graphical_results_static')}</Text>
            </View>
            <PDFTable columns={[t('output_static_vertical_stability_checks'), t('output_symbol'), t('output_value')]} data={stabilityChecks} />
            <Text style={styles.boldGreen}>{t('note_for_vertical_results')}</Text>
            <BasinDesignResults project={project} projectInfo={projectInfo} results={results} />
            <PDFTable columns={[t('output_static_vertical_soil_thrust'), t('output_symbol'), t('output_value'), t('output_unit')]} data={soilThrust} />
            <PDFTable columns={[t('output_static_vertical_weights'), t('output_symbol'), t('output_value'), t('output_unit')]} data={weights} />
            <PDFTable columns={[t('output_static_vertical_resulting_forces_moments'), t('output_symbol'), t('output_value'), t('output_unit')]} data={resultingForcesMoments} />
            <PDFTable columns={[t('output_static_vertical_hydrostatic_pressure'), t('output_symbol'), t('output_value'), t('output_unit')]} data={hydrostaticPressure} />
            <PDFTable columns={[t('output_static_vertical_uplift_pressure'), t('output_symbol'), t('output_value'), t('output_unit')]} data={upliftPressure} />
        </View>
    );
};

export default PDFStaticVertical;
